import { jsx as _jsx } from "react/jsx-runtime";
import { getCmsHubRootUrl, handleRedirect } from 'pricing-pages-lib/utils/urls';
import SalesServiceSidegradeRedirectButton from 'pricing-pages-ui/components/auth-cta-motions/SalesServiceSidegradeRedirectButton';
import { useSelector } from 'react-redux';
import { ENTERPRISE, PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING } from 'self-service-api/constants/ProductLines';
import { SALES_HUB_STARTER, SERVICE_HUB_STARTER } from 'self-service-api/constants/ProductNames';
import VersionNumbers from 'self-service-api/constants/VersionNumbers';
import PurchaseMotionButton from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionButton';
import * as PurchaseMotionTypes from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS, BUY_TOUCHLESS, CALCULATE_PRICE, CALCULATE_CONTACTS, CALCULATE_SEATS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { INTERSTITIAL } from '../../../constants/tracking/Locations';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { hasProductVersionInProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { productForSkuIdSelector, productVersionSelector } from '../../../selectors/ProductsSelectors';
import InterstitialPurchaseMotionButtonWrapper from './InterstitialPurchaseMotionButtonWrapper';
import MarketingPurchaseMotionButtonWrapper from './MarketingPurchaseMotionButtonWrapper';
import { filteredPropsForPBM, getPurchaseMotionForLevel } from './PurchaseMotionButtonWrapper';
export const useMarketingPMBOverride = propsToPass => {
  const {
    productConfigurations
  } = propsToPass;
  const purchaseMotion = getPurchaseMotionForLevel(propsToPass);
  const isSeatsUngated = useAppSelector(state => isUngatedForSeatsSelector(state));
  const isAppAuth = useAppSelector(state => isAppAuthSelector(state));
  const isTouchlessPurchase = [BUY_TOUCHLESS, BUY_ADDITIONAL_CONTACTS].includes(purchaseMotion);
  const marketingProductFromConfig = useSelector(state => {
    return productConfigurations && productConfigurations.map(({
      skuId
    }) => productForSkuIdSelector(state, {
      skuId
    })).find(({
      productLine
    }) => productLine === MARKETING);
  }); // Seats users in-app should never see the Contacts Confirmation Page

  if (isSeatsUngated && isAppAuth && isTouchlessPurchase) {
    return null;
  }

  if (purchaseMotion === BUY_TOUCHLESS && marketingProductFromConfig || purchaseMotion === BUY_ADDITIONAL_CONTACTS) {
    return /*#__PURE__*/_jsx(MarketingPurchaseMotionButtonWrapper, Object.assign({}, propsToPass));
  } // For Marketing Pro Trials we want to hide the redirect survey and send them to the trial guide


  if (marketingProductFromConfig && marketingProductFromConfig.productLevel === PROFESSIONAL && purchaseMotion === PurchaseMotionTypes.TRIAL) {
    return /*#__PURE__*/_jsx(PurchaseMotionButton, Object.assign({}, filteredPropsForPBM(propsToPass), {
      shouldHideSurvey: true
    }));
  }

  return null;
};

const useSalesServiceMarch2020Override = ({
  propsToPass,
  apiName
}) => {
  const isSeatsUngated = useAppSelector(state => isUngatedForSeatsSelector(state));
  const purchaseMotion = getPurchaseMotionForLevel(propsToPass);
  const march2020Sku = useSelector(state => productVersionSelector(state, {
    apiName,
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    versionNumber: VersionNumbers[apiName].MARCH_2020
  }));
  const march2020SkuId = march2020Sku && march2020Sku.skuId || null;
  const {
    productConfigurations
  } = propsToPass;
  const checkingOut2020Version = productConfigurations && productConfigurations.some(({
    skuId: searchSkuId
  }) => searchSkuId === march2020SkuId);
  const ownsLegacyVersion = useSelector(state => hasProductVersionInProductInventorySelector(state, {
    apiName,
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    versionNumber: VersionNumbers[apiName].LEGACY
  }));
  const product = useSelector(state => {
    if (march2020SkuId) {
      return productForSkuIdSelector(state, {
        skuId: march2020SkuId
      });
    }

    return null;
  });
  const productLine = product && product.productLine;

  if (!isSeatsUngated && productLine && march2020SkuId && purchaseMotion === PurchaseMotionTypes.BUY_TOUCHLESS && ownsLegacyVersion && checkingOut2020Version) {
    return /*#__PURE__*/_jsx(SalesServiceSidegradeRedirectButton, Object.assign({
      productLine: productLine,
      skuId: march2020SkuId
    }, propsToPass));
  }

  return null;
};

export const useSalesPMBOverride = propsToPass => useSalesServiceMarch2020Override({
  propsToPass,
  apiName: SALES_HUB_STARTER
});
export const useServicePMBOverride = propsToPass => useSalesServiceMarch2020Override({
  propsToPass,
  apiName: SERVICE_HUB_STARTER
});
export const useCmsPMBOverride = propsToPass => {
  const configsContainCMSEnterprise = useSelector(state => {
    const {
      productConfigurations
    } = propsToPass;
    return productConfigurations && productConfigurations.some(({
      skuId
    }) => productForSkuIdSelector(state, {
      skuId
    }).productLine === CMS && productForSkuIdSelector(state, {
      skuId
    }).productLevel === ENTERPRISE);
  });
  const purchaseMotion = getPurchaseMotionForLevel(propsToPass);

  if (configsContainCMSEnterprise && (purchaseMotion === PurchaseMotionTypes.TRIAL || purchaseMotion === PurchaseMotionTypes.ACTIVATE)) {
    return /*#__PURE__*/_jsx(PurchaseMotionButton, Object.assign({}, filteredPropsForPBM(propsToPass), {
      onSuccess: () => handleRedirect(getCmsHubRootUrl())
    }));
  }

  return null;
};
export const useSeatsInterstitialOverride = propsToPass => {
  const isSeatsUngated = useAppSelector(state => isUngatedForSeatsSelector(state));
  const isNotOnInterstitial = propsToPass.trackingSource !== INTERSTITIAL;
  const isAppAuth = useAppSelector(state => isAppAuthSelector(state));
  const purchaseMotion = getPurchaseMotionForLevel(propsToPass);
  const isTouchlessPurchase = [BUY_TOUCHLESS, BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS, CALCULATE_PRICE, CALCULATE_CONTACTS, CALCULATE_SEATS].includes(purchaseMotion);

  if (isAppAuth && isSeatsUngated && isNotOnInterstitial && isTouchlessPurchase) {
    return /*#__PURE__*/_jsx(InterstitialPurchaseMotionButtonWrapper, Object.assign({}, propsToPass));
  }

  return null;
};
export const usePMBOverride = propsToPass => {
  const seatsInterstitialOverride = useSeatsInterstitialOverride(propsToPass);
  const marketingOverride = useMarketingPMBOverride(propsToPass);
  const salesOverride = useSalesPMBOverride(propsToPass);
  const serviceOverride = useServicePMBOverride(propsToPass);
  const cmsOverride = useCmsPMBOverride(propsToPass);
  return seatsInterstitialOverride || marketingOverride || salesOverride || serviceOverride || cmsOverride;
};